import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

const AuthCtx = createContext(null);

function useNotificationsSocket(user) {
  useEffect(() => {
    if (!user) {
      return undefined;
    }
    let ws;
    let closed = false;
    const connect = () => {
      ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/ws`);
      ws.onmessage = (ev) => {
        const data = JSON.parse(ev.data);
        if (data.type === "notification") {
          toast(data.title, { description: data.body });
          window.dispatchEvent(new CustomEvent("selvix-notification"));
        }
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      closed = true;
      ws?.close();
    };
  }, [user]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);

  const reloadAccess = useCallback(() => api.get("/access/me").then(({ data }) => setAccess(data)).catch(() => {}), []);
  useEffect(() => {
    if (user !== null) reloadAccess();
  }, [user, reloadAccess]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  useNotificationsSocket(user);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, []);
  const value = useMemo(() => ({ user, setUser, loading, refresh, logout, access, reloadAccess }), [user, loading, refresh, logout, access, reloadAccess]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
